import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, Plus, MoreVertical, CheckCircle2 } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShell from '@/components/PortalShell';

const statusTabs = [
  { label: 'All', count: 43, active: true },
  { label: 'Active', count: 28 },
  { label: 'Inactive', count: 6 },
  { label: 'Pending', count: 9 },
];

const users = [
  { name: 'Ahmed Ali',         facility: 'Burjeel Al Nahyan',   others: 1, status: 'Active',  dateAdded: "15 Nov' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "17 Nov' 25" },
  { name: 'Fatima Al Ali',     facility: 'Burjeel - Mussafah',  others: 2, status: 'Active',  dateAdded: "5 Nov' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Nov' 25" },
  { name: 'Aisha Al Mansoori', facility: 'Burjeel Royal',       others: 0, status: 'Inactive',dateAdded: "26 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "30 Oct' 25" },
  { name: 'Khalid Al Ameri',   facility: 'Burjeel Al Nahyan',   others: 1, status: 'Pending', dateAdded: "15 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "19 Oct' 25" },
  { name: 'Sara Al Mulla',     facility: 'Burjeel - Mussafah',  others: 2, status: 'Active',  dateAdded: "10 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "12 Oct' 25" },
  { name: 'Nasser Al Neyadi',  facility: 'Burjeel Day Surgery', others: 0, status: 'Active',  dateAdded: "2 Oct' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Oct' 25" },
  { name: 'Amna Al Mazrouei',  facility: 'Burjeel Royal',       others: 1, status: 'Pending', dateAdded: "28 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "1 Oct' 25" },
  { name: 'Ahmed Shareef',     facility: 'Burjeel - Mussafah',  others: 1, status: 'Pending', dateAdded: 'Just now',    lastLogin: '—',           lastLoginTime: '',         lastModified: 'Just now', isNew: true },
];

function StatusBadge({ status }) {
  const styles = { Active: 'bg-emerald-50 text-emerald-600 border-emerald-200', Inactive: 'bg-gray-50 text-gray-500 border-gray-200', Pending: 'bg-amber-50 text-amber-600 border-amber-200' };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>{status}</span>;
}

export default function UserCreatedScene() {
  return (
    <PortalShell activeItem="User">
      <motion.div
        data-testid="scene-user-created"
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="mx-8 mt-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3"
      >
        <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" />
        <p className="font-body text-sm text-emerald-800">
          We've sent <span className="font-bold">Ahmed Shareef</span> a sign-in link. Once they confirm their details, you'll be able to continue.
          <span className="text-emerald-600 ml-1 text-xs font-medium">(Invite valid for 3 days)</span>
        </p>
        <InfoSpot text="An email with a sign-in link has been sent. They must click the link, verify via OTP, confirm their profile, then they can log in. If not accepted within 3 days, the invite expires." position="bottom" color="teal" />
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="px-8 pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl text-[#0A0A0A]">Users</h1>
            <p className="font-body text-sm text-[#5F6368] mt-1">Manage the users and their roles & permissions</p>
          </div>
          <button className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-body font-medium"><Plus size={16} /> Add new user</button>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            {statusTabs.map(tab => (
              <button key={tab.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-body ${tab.active ? 'bg-[#0A0A0A] text-white' : 'bg-[#F5F5F5] text-[#5F6368]'}`}>
                {tab.label} <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab.active ? 'bg-white/20' : 'bg-[#E5E7EB]'}`}>{tab.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2"><Search size={16} className="text-[#5F6368]" /><SlidersHorizontal size={16} className="text-[#5F6368]" /></div>
        </div>
      </motion.div>

      <div className="px-8">
        <table className="w-full">
          <thead><tr className="border-b border-[#E5E7EB]">
            {['User name', 'Facility', 'Status', 'Date added', 'Last login', 'Last modified', ''].map(h => (
              <th key={h} className="text-left py-2.5 text-[12px] font-body font-medium text-[#5F6368]">{h}</th>
            ))}
          </tr></thead>
          <tbody>{users.map((u, i) => (
            <motion.tr key={u.name} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.04 }}
              className={`border-b border-[#F5F5F5] ${u.isNew ? 'bg-teal-50/40' : ''}`}>
              <td className={`py-3 text-[13px] font-body ${u.isNew ? 'font-bold text-teal-700' : 'font-medium text-[#0A0A0A]'}`}>{u.name}</td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">{u.facility} <span className="text-teal-600 text-[11px]">+{u.others} others</span></td>
              <td className="py-3"><StatusBadge status={u.status} /></td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">{u.dateAdded}</td>
              <td className="py-3"><p className="text-[13px] font-body text-[#5F6368]">{u.lastLogin}</p>{u.lastLoginTime && <p className="text-[11px] font-body text-[#5F6368]/60">{u.lastLoginTime}</p>}</td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">{u.lastModified}</td>
              <td className="py-3"><MoreVertical size={14} className="text-[#5F6368]" /></td>
            </motion.tr>
          ))}</tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-8 py-3 border-t border-[#E5E7EB]">
        <span className="text-[12px] font-body text-[#5F6368]">Page 1 of 5</span>
        <div className="flex items-center gap-1">
          <button className="px-2.5 py-1 text-[12px] font-body text-[#5F6368]">&lt; Previous</button>
          <button className="w-7 h-7 rounded-md bg-[#0A0A0A] text-white text-[12px] font-body font-medium">1</button>
          <button className="w-7 h-7 rounded-md text-[12px] font-body text-[#5F6368]">2</button>
          <button className="w-7 h-7 rounded-md text-[12px] font-body text-[#5F6368]">3</button>
          <span className="text-[12px] text-[#5F6368]">...</span>
          <button className="px-2.5 py-1 text-[12px] font-body text-[#5F6368]">Next &gt;</button>
        </div>
      </div>
    </PortalShell>
  );
}
